import { townLabel } from '../town.ts';
import { useState } from 'react';
import {
  usePlacementForm, useRecordMove, useRecordStay, useEndStay, useConvertStay,
  type PlacementForm, type MoveInput,
} from '../api/placements.ts';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';
import { startStatement } from '../placementStart.ts';
import { PastAddresses } from './PastAddresses.tsx';
import { WhereaboutsReports } from './WhereaboutsReports.tsx';
import { useWhereaboutsReports } from '../api/whereaboutsReports.ts';
import { useClientSummary } from '../api/navigation.ts';
import { LivesAtLines } from './LivesAtLines.tsx';
import type { PlacementStart } from '../api/types.ts';
import type { FacilityOption } from '../api/places.ts';
import {
  FacilityPicker, HomeFields, EMPTY_HOME, homeInput, type HomeDraft,
  HouseholdFields, EMPTY_HOUSEHOLD, householdInput, type HouseholdDraft,
} from './PlacePicker.tsx';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';

/** Today as a calendar day in the viewer's zone — never via toISOString. */
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

type Form = 'move' | 'stay' | 'convert' | null;

/**
 * Where the client lives, and where they are — which are two different
 * questions, and the panel never lets them blur.
 *
 * A MOVE changes where someone lives. A STAY does not: the flat is still the
 * flat, the bed may still be held, and ending the stay restores nothing because
 * nothing was closed.
 *
 * **Ending a stay offers one outcome.** `temporary_stay.outcome` has five
 * values, only three are reachable, and only 'Returned' is a choice anybody
 * makes here — the other two are written by recording the move or the death.
 * A dropdown offering all five would invite someone to record a death on a
 * stay form, which records a death nowhere and leaves the client's legal status
 * saying they are alive.
 *
 * Buttons appear only when the server said `may_record` — `social_work` at
 * write or full: tiers 1 and 2, both case-manager roles and, since 057, the
 * Director of Social Work and the Paralegal.
 *
 * **Here the kind of placement is asked first**, because here the person IS
 * choosing what kind of place someone is going to, and Rehab and Nursing Home
 * share a building type. Intake asks the other way round and derives the kind
 * from the building (061). Both end at the same pair, a kind whose facility
 * type is the building's, which is what `move_client()` enforces.
 */
export function WhereaboutsPanel({ clientId, placementStart = null }: { clientId: string; placementStart?: PlacementStart | null }) {
  const form = usePlacementForm(clientId);
  const reports = useWhereaboutsReports(clientId);
  // A-#772 3 (A-#123): the address with its full town line, from the summary's lives_at, as the Client card shows it.
  const summary = useClientSummary(clientId);
  const [open, setOpen] = useState<Form>(null);

  if (form.isLoading) {
    return <Shell><p className="text-neutral-600">Loading where they are…</p></Shell>;
  }
  if (!form.data) {
    return <Shell><p className="text-neutral-500">Where they are could not be loaded.</p></Shell>;
  }

  const w = form.data.whereabouts;
  const onStay = Boolean(w?.temporary_stay_id);

  return (
    <Shell>
      {w?.lives_at ? (
        <p className="text-neutral-900">
          <span className="text-neutral-600">Lives at </span>
          {/* w.lives_at still decides that there is a placement; the words are the summary's address once it has
              answered, and the view's facility name or street until then */}
          <span className="font-semibold">
            {summary.data?.lives_at ? <LivesAtLines at={summary.data.lives_at} showAway={false} /> : w.lives_at}
          </span>
          {w.placement_kind && <span className="text-neutral-600"> · {w.placement_kind}</span>}
          <SinceText w={w} start={placementStart} />
        </p>
      ) : reports.data?.whereabouts_unknown ? (
        /* Whereabouts unknown: no placement is the truth, and the report below
           says what the agency does know (093). */
        <p className="text-neutral-700">No current placement.</p>
      ) : (
        <p className="text-neutral-700">
          <span className="font-semibold text-amber-800">No placement recorded.</span>{' '}
          Record where they live before recording a stay.
        </p>
      )}

      {onStay && w && <Away w={w} clientId={clientId} mayRecord={form.data.may_record} />}

      {form.data.may_record && open === null && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!onStay && (
            <>
              <button type="button" onClick={() => setOpen('move')} className={secondary}>
                Record a move
              </button>
              {w?.lives_at && (
                <button type="button" onClick={() => setOpen('stay')} className={secondary}>
                  Record a temporary stay
                </button>
              )}
            </>
          )}
          {/* While a stay is open, a move IS a conversion — the stay closes with
              its own outcome rather than being left hanging. Offering both
              would be offering the same act twice, one of which loses the
              hospital period. */}
          {onStay && (
            <button type="button" onClick={() => setOpen('convert')} className={secondary}>
              The stay is permanent — record the move
            </button>
          )}
        </div>
      )}

      {open === 'move' && (
        <MoveForm clientId={clientId} catalogue={form.data} kind="move" onDone={() => setOpen(null)} />
      )}
      {open === 'convert' && (
        <MoveForm clientId={clientId} catalogue={form.data} kind="convert" onDone={() => setOpen(null)} />
      )}
      {open === 'stay' && (
        <StayForm clientId={clientId} catalogue={form.data} onDone={() => setOpen(null)} />
      )}

      <WhereaboutsReports clientId={clientId} mayRecord={form.data.may_record} />
      <PastAddresses clientId={clientId} mayRecord={form.data.may_record} />
    </Shell>
  );
}

/**
 * When they came to live here, as the record allows it to be said (092). The
 * whereabouts row's date is the placement's started_on, which on an asserted
 * start is an effective date: it is never shown bare as if it were known.
 */
function SinceText({ w, start }: { w: { placed_on: string | null }; start: PlacementStart | null }) {
  const st = startStatement(start);
  if (st.kind === 'asserted' || st.kind === 'no-move') return <span className="text-neutral-600"> · {st.text}</span>;
  if (w.placed_on) return <span className="text-neutral-600"> since {formatDate(w.placed_on)}</span>;
  return null;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="panel">
      <h2 className="panel-bar">
        Where they are
      </h2>
      <div className="px-4 pt-1 pb-4">{children}</div>
    </section>
  );
}

/** An open stay, and the one button that ends it. */
function Away({
  w, clientId, mayRecord,
}: {
  w: NonNullable<PlacementForm['whereabouts']>;
  clientId: string;
  mayRecord: boolean;
}) {
  const end = useEndStay(clientId);
  const overdue =
    w.expected_return_on !== null && w.expected_return_on < todayLocal();

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <p className="text-neutral-900">
        <span className="font-semibold">Away at {w.currently_at}</span>
        <span className="text-neutral-700">
          {' '}· {w.stay_type}
          {w.away_since ? ` since ${formatDate(w.away_since)}` : ''}
          {w.days_away !== null ? ` · ${w.days_away} ${w.days_away === 1 ? 'day' : 'days'}` : ''}
        </span>
      </p>
      <p className="mt-0.5 text-sm text-neutral-700">
        {w.stay_reason && <>{w.stay_reason} · </>}
        {w.bed_held ? 'Bed held' : 'Bed not held'}
        {w.expected_return_on && (
          <span className={overdue ? 'font-semibold text-amber-900' : ''}>
            {' '}· expected back {formatDate(w.expected_return_on)}
            {overdue ? ' — overdue' : ''}
          </span>
        )}
      </p>
      {mayRecord && (
        <div className="mt-2">
          <button
            type="button"
            disabled={end.isPending}
            onClick={() => end.mutate({ ended_on: todayLocal() })}
            className={secondary}
          >
            {end.isPending ? 'Saving…' : 'They have returned'}
          </button>
        </div>
      )}
      <Failure error={end.error} />
    </div>
  );
}

function MoveForm({
  clientId, catalogue, kind, onDone,
}: {
  clientId: string;
  catalogue: PlacementForm;
  kind: 'move' | 'convert';
  onDone: () => void;
}) {
  const move = useRecordMove(clientId);
  const convert = useConvertStay(clientId);
  const run = kind === 'convert' ? convert : move;

  const [kindCode, setKindCode] = useState('');
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  const [homeId, setHomeId] = useState('');
  const [home, setHome] = useState<HomeDraft>(EMPTY_HOME);
  const [household, setHousehold] = useState<HouseholdDraft>(EMPTY_HOUSEHOLD);
  const [movedOn, setMovedOn] = useState(todayLocal());
  const [reason, setReason] = useState('');

  const chosen = catalogue.placement_kinds.find((k) => k.kind_code === kindCode) ?? null;
  const toHome = chosen?.target_type === 'Property';
  // 090: lives with family. Its own branch, never the building search and never a property.
  const toHousehold = chosen?.target_type === 'Household';
  const NEW_HOME = 'new';

  // The same rule move_client() enforces, so the form cannot offer a choice the
  // function will refuse: a facility kind searches buildings of ITS type, a
  // home kind takes this client's own properties or a home recorded now.
  const newHome = toHome && homeId === NEW_HOME ? homeInput(home) : null;
  const newHousehold = toHousehold && homeId === NEW_HOME ? householdInput(household) : null;
  const target: Pick<MoveInput, 'target_id' | 'new_home' | 'new_household'> | null = !chosen
    ? null
    : toHome
      ? homeId === NEW_HOME
        ? newHome ? { new_home: newHome } : null
        : homeId ? { target_id: homeId } : null
      : toHousehold
        ? homeId === NEW_HOME
          ? newHousehold ? { new_household: newHousehold } : null
          : homeId ? { target_id: homeId } : null
        : facility ? { target_id: facility.id } : null;

  return (
    <form
      className="mt-3 border-t border-neutral-200 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!kindCode || !target || movedOn.length !== 10) return;
        const body: MoveInput = {
          kind_code: kindCode,
          ...target,
          moved_on: movedOn,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        };
        run.mutate(body, { onSuccess: onDone });
      }}
    >
      <label className={label} htmlFor="move-kind">What kind of placement</label>
      <select
        id="move-kind"
        className={field}
        value={kindCode}
        onChange={(e) => { setKindCode(e.target.value); setFacility(null); setHomeId(''); }}
      >
        <option value="">Choose…</option>
        {catalogue.placement_kinds.map((k) => (
          <option key={k.kind_code} value={k.kind_code}>{k.kind_label}</option>
        ))}
      </select>

      <div className="mt-3">
        {!chosen ? (
          <p className="text-sm text-neutral-600">Choose a kind of placement first, then where.</p>
        ) : toHousehold ? (
          <fieldset>
            <legend className={label}>Which family member's home</legend>
            <div className="mt-1 space-y-1">
              {catalogue.households.map((h) => (
                <label key={h.id} className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                  homeId === h.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`}>
                  <input type="radio" name="move-household" checked={homeId === h.id}
                         onChange={() => setHomeId(h.id)} className="size-5 shrink-0" />
                  <span>
                    <span className="font-medium text-neutral-900">{h.street ?? '(no street)'}</span>
                    <span className="block text-sm text-neutral-600">
                      {[townLabel(h.city, h.neighbourhood, h.neighbourhood_source), h.residence_type, h.notes].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </label>
              ))}
              <label className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                homeId === NEW_HOME ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`}>
                <input type="radio" name="move-household" checked={homeId === NEW_HOME}
                       onChange={() => setHomeId(NEW_HOME)} className="size-5 shrink-0" />
                <span className="font-medium text-neutral-900">
                  {catalogue.households.length === 0
                    ? "Record the family member's home"
                    : "Somewhere else: record the family member's home"}
                </span>
              </label>
            </div>
            {homeId === NEW_HOME && (
              <div className="mt-2">
                <HouseholdFields draft={household} onChange={setHousehold} />
              </div>
            )}
          </fieldset>
        ) : toHome ? (
          <fieldset>
            <legend className={label}>Which home</legend>
            <div className="mt-1 space-y-1">
              {catalogue.properties.map((p) => (
                <label key={p.id} className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                  homeId === p.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`}>
                  <input type="radio" name="move-home" checked={homeId === p.id}
                         onChange={() => setHomeId(p.id)} className="size-5 shrink-0" />
                  <span>
                    <span className="font-medium text-neutral-900">{p.name}</span>
                    <span className="block text-sm text-neutral-600">
                      {[p.street, townLabel(p.city, p.neighbourhood, p.neighbourhood_source)].filter(Boolean).join(', ')}
                    </span>
                  </span>
                </label>
              ))}
              <label className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                homeId === NEW_HOME ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`}>
                <input type="radio" name="move-home" checked={homeId === NEW_HOME}
                       onChange={() => setHomeId(NEW_HOME)} className="size-5 shrink-0" />
                <span className="font-medium text-neutral-900">
                  {catalogue.properties.length === 0
                    ? 'Record their home: nothing is on file yet'
                    : 'Somewhere else: record their new home'}
                </span>
              </label>
            </div>
            {homeId === NEW_HOME && (
              <div className="mt-2">
                <HomeFields draft={home} onChange={setHome} ownerName={catalogue.home_owner_name} />
              </div>
            )}
          </fieldset>
        ) : (
          <FacilityPicker
            key={chosen.kind_code}
            id="move-facility"
            searchLabel="Where"
            selected={facility}
            onSelect={setFacility}
            lockedType={chosen.facility_type ? { code: chosen.facility_type, kindLabel: chosen.kind_label } : null}
          />
        )}
      </div>

      <label className={`${label} mt-3`} htmlFor="move-date">Moved on</label>
      <input id="move-date" type="date" className={field} value={movedOn}
             onChange={(e) => setMovedOn(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="move-reason">Why</label>
      <input id="move-reason" className={field} value={reason} maxLength={160}
             onChange={(e) => setReason(e.target.value)}
             placeholder="Home no longer safe, hospital discharge, family request…" />

      {chosen && (
        <p className="mt-2 text-sm text-neutral-600">
          {chosen.is_community
            ? 'This is a community placement — the visit rate follows the residence type from the move date.'
            : 'The visit rate follows the residence type from the move date.'}
          {kind === 'convert' && ' The stay closes with the move, and the period away stays on the record.'}
        </p>
      )}

      <Failure error={run.error} />
      <Actions
        busy={run.isPending}
        disabled={!kindCode || !target}
        verb={kind === 'convert' ? 'Record the move' : 'Record the move'}
        onCancel={onDone}
      />
    </form>
  );
}

function StayForm({
  clientId, catalogue, onDone,
}: { clientId: string; catalogue: PlacementForm; onDone: () => void }) {
  const stay = useRecordStay(clientId);
  const [stayType, setStayType] = useState('');
  const [where, setWhere] = useState<'facility' | 'other'>('facility');
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  const [other, setOther] = useState('');
  const [startedOn, setStartedOn] = useState(todayLocal());
  const [expected, setExpected] = useState('');
  const [reason, setReason] = useState('');

  const ready =
    stayType !== '' &&
    startedOn.length === 10 &&
    (where === 'facility' ? facility !== null : other.trim() !== '');

  return (
    <form
      className="mt-3 border-t border-neutral-200 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        stay.mutate(
          {
            stay_type: stayType,
            started_on: startedOn,
            ...(where === 'facility' && facility ? { facility_id: facility.id } : { other_location: other.trim() }),
            ...(expected.length === 10 ? { expected_return_on: expected } : {}),
            ...(reason.trim() ? { reason: reason.trim() } : {}),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <label className={label} htmlFor="stay-type">What kind of stay</label>
      <select id="stay-type" className={field} value={stayType}
              onChange={(e) => setStayType(e.target.value)}>
        <option value="">Choose…</option>
        {catalogue.stay_types.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>

      <fieldset className="mt-3">
        <legend className={label}>Where</legend>
        <div className="mt-1 flex gap-2">
          <button type="button" onClick={() => setWhere('facility')}
                  className={`${secondary} ${where === 'facility' ? '!bg-neutral-900 !text-white' : ''}`}>
            A facility
          </button>
          <button type="button" onClick={() => setWhere('other')}
                  className={`${secondary} ${where === 'other' ? '!bg-neutral-900 !text-white' : ''}`}>
            Somewhere else
          </button>
        </div>
      </fieldset>

      {where === 'facility' ? (
        <FacilityPicker id="stay-facility" searchLabel="Which facility"
                        selected={facility} onSelect={setFacility} />
      ) : (
        <input className={field} value={other} maxLength={160} aria-label="Where"
               onChange={(e) => setOther(e.target.value)}
               placeholder="A daughter's house, a shelter, a named address…" />
      )}

      <label className={`${label} mt-3`} htmlFor="stay-start">Went on</label>
      <input id="stay-start" type="date" className={field} value={startedOn}
             onChange={(e) => setStartedOn(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="stay-expected">
        Expected back <span className="font-normal text-neutral-500">— if known</span>
      </label>
      <input id="stay-expected" type="date" className={field} value={expected}
             onChange={(e) => setExpected(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="stay-reason">Why</label>
      <input id="stay-reason" className={field} value={reason} maxLength={160}
             onChange={(e) => setReason(e.target.value)}
             placeholder="Pneumonia, hip fracture, respite…" />

      <p className="mt-2 text-sm text-neutral-600">
        Their placement is not changed — they still live at{' '}
        {catalogue.whereabouts?.lives_at ?? 'their recorded address'}, and the bed is
        held unless somebody records otherwise.
      </p>

      <Failure error={stay.error} />
      <Actions busy={stay.isPending} disabled={!ready} verb="Record the stay" onCancel={onDone} />
    </form>
  );
}

function Actions({
  busy, disabled, verb, onCancel,
}: { busy: boolean; disabled: boolean; verb: string; onCancel: () => void }) {
  return (
    <div className="mt-3 flex gap-2">
      <button type="submit" disabled={busy || disabled}
              className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                         disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
        {busy ? 'Saving…' : verb}
      </button>
      <button type="button" onClick={onCancel} className={secondary}>Cancel</button>
    </div>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
      {error.message}
      {api?.hint && <span className="block text-rose-700">{api.hint}</span>}
    </p>
  );
}
